/**
 * TextReaderFeature - Text-to-speech using Web Speech API
 */
import BaseFeature from './BaseFeature.js';

export default class TextReaderFeature extends BaseFeature {
    constructor(widget, options = {}) {
        super(widget, options);
        this.synth = window.speechSynthesis || null;
        this.utterance = null;
        this.isPlaying = false;
        this.isMuted = false;
        this.sections = [];
        this.currentSectionIndex = 0;
        this.defaultSpeed = options.defaultSpeed || 1.0;
        this.defaultVolume = options.defaultVolume || 0.8;
        this.highlightText = options.highlightText !== false;
        this._highlightedEl = null;
        this.isPaused = false;
        this._autoAdvance = true;
    }

    enable() {
        super.enable();
        if (!this.synth) {
            console.warn('[A11Y] Speech synthesis not supported');
            return;
        }
        this.addBodyClass('dhA11y-text-reader-active');
        this._collectSections();
        this._bindSelection();
        this._bindClickToRead();
        this._bindKeyboardFocusRead();
    }

    disable() {
        super.disable();
        this.stop();
        this.removeBodyClass('dhA11y-text-reader-active');
        this._unbindSelection();
        this._unbindClickToRead();
        this._unbindKeyboardFocusRead();
        this._clearHighlight();
    }

    /**
     * Collect readable sections from the page
     */
    _collectSections() {
        const selectors = 'main p, main h1, main h2, main h3, main h4, main h5, main h6, main li, main td, main blockquote, main a[href], main span[itemprop="headline"], article p, article h1, article h2, article h3, article h4, article h5, article h6, article li, article a[href], article span[itemprop="headline"], .content p, .content h1, .content h2, .content h3, .content h4, .content h5, .content h6, .content a[href]';
        const allEls = Array.from(document.querySelectorAll(selectors)).filter(el => {
            const text = el.textContent.trim();
            return text.length > 0 && el.offsetParent !== null;
        });
        // Remove child elements whose text is already covered by a parent in the list
        this.sections = allEls.filter((el, _i, arr) => {
            return !arr.some(other => other !== el && other.contains(el));
        });
        this.currentSectionIndex = 0;
    }

    /**
     * Bind text selection for reading
     */
    _bindSelection() {
        this._selectionHandler = () => {
            const selection = window.getSelection();
            if (selection && selection.toString().trim().length > 0) {
                // User selected text - ready to read on play
                this._selectedText = selection.toString().trim();
            }
        };
        document.addEventListener('mouseup', this._selectionHandler);
    }

    /**
     * Unbind text selection
     */
    _unbindSelection() {
        if (this._selectionHandler) {
            document.removeEventListener('mouseup', this._selectionHandler);
            this._selectionHandler = null;
        }
    }

    /**
     * Bind click-to-read: clicking on a readable element jumps to it and starts reading
     */
    _bindClickToRead() {
        this._clickToReadHandler = (e) => {
            // Ignore clicks on widget UI and control bar
            if (e.target.closest('#accessibility-widget') || e.target.closest('.accessibility-control-bar')) return;

            // Ignore if user is selecting text
            const selection = window.getSelection();
            if (selection && selection.toString().trim().length > 0) return;

            // Find the closest readable section element from click target
            const clickedEl = this._findClosestSection(e.target);
            if (clickedEl !== null) {
                const wasPlaying = this.isPlaying;
                this.currentSectionIndex = clickedEl;
                this.stop();
                if (wasPlaying) {
                    this.play();
                } else {
                    // Just highlight and scroll without playing
                    if (this.sections[this.currentSectionIndex]) {
                        this._highlightElement(this.sections[this.currentSectionIndex]);
                        this._scrollToCurrentSection();
                    }
                }
            }
        };
        document.addEventListener('click', this._clickToReadHandler);
    }

    /**
     * Unbind click-to-read
     */
    _unbindClickToRead() {
        if (this._clickToReadHandler) {
            document.removeEventListener('click', this._clickToReadHandler);
            this._clickToReadHandler = null;
        }
    }

    /**
     * Bind keyboard focus read: when user Tabs to an element, read it aloud
     */
    _bindKeyboardFocusRead() {
        this._isTabbing = false;
        this._keydownForFocusHandler = (e) => {
            if (e.key === 'Tab') this._isTabbing = true;
        };
        this._focusReadHandler = (e) => {
            if (!this._isTabbing) return;
            this._isTabbing = false;

            // Ignore widget UI
            if (e.target.closest('#accessibility-widget') || e.target.closest('.accessibility-control-bar')) return;

            const idx = this._findClosestSection(e.target);
            if (idx !== null) {
                this.currentSectionIndex = idx;
                this.stop();
                this.play();
            } else {
                // Read focused element text even if not in sections
                const text = (e.target.getAttribute('aria-label') || e.target.textContent || '').trim();
                if (text.length > 0) {
                    this.stop();
                    this._selectedText = text;
                    this.play();
                }
            }
        };
        document.addEventListener('keydown', this._keydownForFocusHandler);
        document.addEventListener('focusin', this._focusReadHandler);
    }

    /**
     * Unbind keyboard focus read
     */
    _unbindKeyboardFocusRead() {
        if (this._keydownForFocusHandler) {
            document.removeEventListener('keydown', this._keydownForFocusHandler);
            this._keydownForFocusHandler = null;
        }
        if (this._focusReadHandler) {
            document.removeEventListener('focusin', this._focusReadHandler);
            this._focusReadHandler = null;
        }
    }

    /**
     * Find the index of the closest section to the clicked element
     */
    _findClosestSection(target) {
        let el = target;
        while (el && el !== document.body) {
            const idx = this.sections.indexOf(el);
            if (idx !== -1) return idx;
            el = el.parentElement;
        }
        // If no exact match, find nearest section by DOM position
        for (let i = 0; i < this.sections.length; i++) {
            if (this.sections[i].contains(target)) return i;
        }
        return null;
    }

    /**
     * Toggle play/pause
     */
    togglePlay() {
        if (this.isPlaying) {
            this.pause();
        } else {
            this.play();
        }
    }

    /**
     * Start or resume reading
     */
    play() {
        if (!this.synth) return;

        // Read selected text or current section
        const text = this._selectedText || this._getCurrentSectionText();
        this._selectedText = null;

        if (!text) return;

        // Cancel any current speech before starting new
        this._autoAdvance = true;
        this.synth.cancel();

        this.utterance = new SpeechSynthesisUtterance(text);
        this.utterance.rate = this.defaultSpeed;
        this.utterance.volume = this.isMuted ? 0 : this.defaultVolume;
        this.utterance.lang = this.widget.i18n.getLanguage() === 'de' ? 'de-DE' : 'en-US';

        // Highlight current section
        if (this.highlightText && this.sections[this.currentSectionIndex]) {
            this._highlightElement(this.sections[this.currentSectionIndex]);
            this._scrollToCurrentSection();
        }

        this.utterance.onend = () => {
            // Only auto-advance if not paused/stopped
            if (!this._autoAdvance) return;
            this._clearHighlight();
            this.isPlaying = false;
            this.isPaused = false;
            // Auto-advance to next section
            if (this.currentSectionIndex < this.sections.length - 1) {
                this.currentSectionIndex++;
                this.play();
            } else {
                this.widget.events.emit('textReader:ended');
            }
        };

        this.utterance.onerror = (e) => {
            if (e.error === 'interrupted') return;
            this._clearHighlight();
            this.isPlaying = false;
            this.isPaused = false;
        };

        this.synth.speak(this.utterance);
        this.isPlaying = true;
        this.isPaused = false;
    }

    /**
     * Pause reading - uses cancel() as Chrome workaround
     * since synth.pause() is unreliable in Chromium browsers
     */
    pause() {
        if (this.synth) {
            this._autoAdvance = false;
            this.synth.cancel();
            this.isPlaying = false;
            this.isPaused = true;
        }
    }

    /**
     * Stop reading completely and reset position
     */
    stop() {
        if (this.synth) {
            this._autoAdvance = false;
            this.synth.cancel();
            this.isPlaying = false;
            this.isPaused = false;
            this._clearHighlight();
        }
    }

    /**
     * Go to previous section
     */
    previousSection() {
        if (this.currentSectionIndex > 0) {
            this.currentSectionIndex--;
            const wasPlaying = this.isPlaying || this.isPaused;
            this.synth.cancel();
            this._clearHighlight();
            if (wasPlaying) {
                this.play();
            } else {
                // Just highlight and scroll without playing
                if (this.sections[this.currentSectionIndex]) {
                    this._highlightElement(this.sections[this.currentSectionIndex]);
                    this._scrollToCurrentSection();
                }
            }
        }
    }

    /**
     * Go to next section
     */
    nextSection() {
        if (this.currentSectionIndex < this.sections.length - 1) {
            this.currentSectionIndex++;
            const wasPlaying = this.isPlaying || this.isPaused;
            this.synth.cancel();
            this._clearHighlight();
            if (wasPlaying) {
                this.play();
            } else {
                // Just highlight and scroll without playing
                if (this.sections[this.currentSectionIndex]) {
                    this._highlightElement(this.sections[this.currentSectionIndex]);
                    this._scrollToCurrentSection();
                }
            }
        }
    }

    /**
     * Set reading speed
     */
    setSpeed(speed) {
        this.defaultSpeed = speed;
        if (this.isPlaying) {
            this.stop();
            this.play();
        }
    }

    /**
     * Set volume (0.0 - 1.0)
     */
    setVolume(volume) {
        this.defaultVolume = Math.max(0, Math.min(1, volume));
        this.isMuted = this.defaultVolume === 0;
        // Volume change requires restart of current utterance
        if (this.isPlaying) {
            const idx = this.currentSectionIndex;
            this.synth.cancel();
            this.currentSectionIndex = idx;
            this.play();
        }
    }

    /**
     * Toggle mute
     */
    toggleMute() {
        this.isMuted = !this.isMuted;
        if (this.isPlaying) {
            const idx = this.currentSectionIndex;
            this.synth.cancel();
            this.currentSectionIndex = idx;
            this.play();
        }
    }

    /**
     * Get text of current section
     */
    _getCurrentSectionText() {
        if (this.sections[this.currentSectionIndex]) {
            return this.sections[this.currentSectionIndex].textContent.trim();
        }
        return null;
    }

    /**
     * Scroll to current section
     */
    _scrollToCurrentSection() {
        const el = this.sections[this.currentSectionIndex];
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    /**
     * Highlight an element being read
     */
    _highlightElement(el) {
        this._clearHighlight();
        el.classList.add('dhA11y-text-reader-highlight');
        this._highlightedEl = el;
    }

    /**
     * Clear highlight
     */
    _clearHighlight() {
        if (this._highlightedEl) {
            this._highlightedEl.classList.remove('dhA11y-text-reader-highlight');
            this._highlightedEl = null;
        }
    }
}
